import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, X, Clock } from "lucide-react";
import { userApi } from "@/lib/api";
import { fmtMoney, fmtDate, METHOD_TYPE_LABEL } from "@/lib/format";
import { StatusBadge, CopyButton, SecureFile } from "@/components/common";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import DisputeDeposit from "@/components/DisputeDeposit";

export const Row = ({ label, children, testId }) => (
  <div className="flex items-start justify-between gap-4 py-2 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right font-medium" data-testid={testId}>{children}</span>
  </div>
);

export function StatusTimeline({ deposit }) {
  const st = deposit.status;
  const bad = ["Rejected", "Failed"].includes(st);
  const steps = [
    { label: "Submitted", done: true, at: deposit.created_at },
    { label: "Under review", done: ["Under Review", "Approved", "Refunded"].includes(st) || (bad && deposit.history?.some((h) => h.status === "Under Review")), at: deposit.history?.find((h) => h.status === "Under Review")?.at },
    { label: bad ? st : st === "Refunded" ? "Approved → Refunded" : "Verified & credited", done: ["Approved", "Refunded"].includes(st) || bad, bad, at: deposit.verified_at },
  ];
  return (
    <ol className="space-y-4" data-testid="deposit-status-timeline">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3">
          <span className={cn("mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border", s.done ? (s.bad ? "border-rose-300 bg-rose-50 text-rose-700" : "border-emerald-300 bg-emerald-50 text-emerald-700") : "bg-white text-muted-foreground")}>
            {s.done ? (s.bad ? <X className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />) : <Clock className="h-3.5 w-3.5" />}
          </span>
          <div>
            <p className={cn("text-sm font-medium", !s.done && "text-muted-foreground")}>{s.label}</p>
            {s.at && s.done && <p className="text-xs text-muted-foreground">{fmtDate(s.at)}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

export function DepositSummary({ d }) {
  return (
    <div className="divide-y">
      <Row label="Request ID"><span className="inline-flex items-center gap-1"><span className="num" data-testid="deposit-detail-id">{d.id}</span><CopyButton value={d.id} label="" testId="deposit-detail-copy-id" variant="ghost" className="h-7 w-7 p-0" /></span></Row>
      <Row label="Status"><StatusBadge status={d.status} /></Row>
      <Row label="Requested amount" testId="deposit-detail-amount"><span className="num">{fmtMoney(d.amount, d.currency)}</span></Row>
      {d.confirmed_amount != null && <Row label="Confirmed amount"><span className="num">{fmtMoney(d.confirmed_amount, d.currency)}</span></Row>}
      <Row label="Currency">{d.currency}</Row>
      <Row label="Payment method">{d.payment_method_name} <span className="text-muted-foreground">· {METHOD_TYPE_LABEL[d.method_type]}</span></Row>
      <Row label="Transaction reference"><span className="num">{d.transaction_reference}</span></Row>
      <Row label="Bonus">
        {d.bonus_amount_credited != null && d.status !== "Pending" && d.status !== "Under Review"
          ? <span className="num text-emerald-700">{fmtMoney(d.bonus_amount_credited, d.currency)} credited</span>
          : <span className="num">{fmtMoney(d.bonus_amount_quoted || 0, d.currency)} {d.bonus_snapshot?.eligible ? `(${d.bonus_snapshot.rate_pct}%)` : "(not eligible)"}</span>}
      </Row>
      <Row label="Submitted">{fmtDate(d.created_at)}</Row>
      {d.verified_at && <Row label="Verified">{fmtDate(d.verified_at)}</Row>}
      {d.status_reason && <Row label="Reason"><span className="text-rose-700">{d.status_reason}</span></Row>}
    </div>
  );
}

export default function DepositDetailsSheet({ id, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["deposits", "detail", id],
    queryFn: () => userApi.get(`/deposits/${id}`).then((r) => r.data),
    enabled: !!id,
    refetchInterval: 15000,
  });
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="deposit-details-sheet">
        <SheetHeader><SheetTitle>Deposit details</SheetTitle></SheetHeader>
        {isLoading || !data ? <Skeleton className="mt-6 h-80" /> : (
          <div className="mt-4 space-y-6">
            <DepositSummary d={data} />
            <div><p className="mb-3 text-sm font-semibold">Progress</p><StatusTimeline deposit={data} /></div>
            <div><p className="mb-2 text-sm font-semibold">Receipt</p><SecureFile url={data.receipt_url} client={userApi} testId="deposit-detail-receipt" /></div>
            {["Pending", "Under Review"].includes(data.status) && (
              <div>
                <p className="mb-2 text-sm font-semibold">Not credited yet?</p>
                <DisputeDeposit deposit={data} />
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
